import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button, Alert, Form, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import { tracksApi } from "../tracks/tracksApi";

import type {
  ApprovedArtifactVersion,
  BulkReprocessResponse,
  CorrectJobMetadataFields,
  IdentificationAttempt,
  LifecycleActionResult,
  ProcessingJob,
  ReconciledFieldRecord,
  ResumeJobRequest,
} from "./processingTypes";
import { MEDIA_SOURCE_OPTIONS, isMediaSource } from "./mediaSource";
import {
  useCorrectJobMetadataMutation,
  extractProcessingErrorMessage,
} from "./processingApi";
import { formatDateTime } from "./processingDisplay";
import modalStyles from "./modalLayout.module.scss";
import { JobRef } from "./JobRefBadge";
import { EntityRefBadge } from "../../components/EntityRef/EntityRefBadge";

// Shared modal layout (owner QA 2026-07-18: Resume Review title bar clipped
// off-screen). Bootstrap `centered` lets a tall dialog extend under the fixed
// navbar/banner (which sit ABOVE the modal in z-order); these props top-align
// the dialog below them and cap its height so the header/footer are always
// visible and only the body scrolls — at every resolution and zoom level.
const SAFE_MODAL_LAYOUT = {
  scrollable: true,
  dialogClassName: modalStyles.dialog,
  contentClassName: modalStyles.content,
} as const;

// U1a — shared lifecycle-action modals (reprocess / resume / revoke / bulk
// results), used by the Processing Jobs list and Job detail pages. Copy for
// each modal follows the owner-approved UX requirements (2026-07-02):
//   Reprocess: creates n+1; prior superseded; MP3 removed after verified cutover.
//   Resume:    why parked, what corrections help, where the pipeline resumes.
//   Delete Song / Archive: Song Library owns governed song lifecycle.

const MONO: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
  wordBreak: "break-all",
};

// ---- Reprocess ---------------------------------------------------------------

export interface ReprocessTarget {
  jobId: string;
  label: string;
  attempt: number;
  trackId?: string | null;
  lineageTrackId?: string | null;
}

export function ReprocessConfirmModal({
  show,
  target,
  busy,
  errorMessage,
  errorReasonCode,
  onCancel,
  onConfirm,
}: {
  show: boolean;
  target: ReprocessTarget | null;
  busy: boolean;
  errorMessage: string;
  /** U1-api reasonCode — `source-intake-missing` gets the re-upload guidance. */
  errorReasonCode?: string;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  // The exact next version is derived server-side (monotonic across the whole
  // Track lineage) — this is a floor, so phrase it as "n+1", not a promise.
  const nextVersion = (target?.attempt ?? 0) + 1;
  const trackId = target?.lineageTrackId || target?.trackId || null;
  const trackless = !trackId;
  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Reprocess song</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {target && (
          <>
            <p className="mb-2">
              Reprocess <strong>{target.label}</strong> from its retained source
              file?
            </p>
            <div className="mb-3">
              <JobRef jobId={target.jobId} prefix="Job" />
            </div>
            <Alert variant="info" className="mb-3" data-testid="reprocess-confirm-copy">
              {trackless ? (
                <ul className="mb-0 ps-3" data-testid="reprocess-copy-trackless">
                  <li>
                    This creates a <strong>new Song Processing job</strong> and
                    sends the retained source file through the full pipeline
                    again.
                  </li>
                  <li>
                    This failed attempt <strong>remains as Processing / audit
                    history</strong> — it is not overwritten.
                  </li>
                  <li>
                    If the new attempt succeeds, KrossKast <strong>creates a
                    Track</strong>. There is no existing Track ID to preserve.
                  </li>
                  <li>
                    On successful recovery, KrossKast will{" "}
                    <strong>automatically archive</strong> this failed parent so
                    Active Song Processing no longer shows a resolved issue.
                  </li>
                </ul>
              ) : (
                <ul className="mb-0 ps-3" data-testid="reprocess-copy-track-linked">
                  <li>
                    This creates a <strong>new artifact version v{nextVersion}</strong>{" "}
                    by re-running the full processing pipeline.
                  </li>
                  <li>
                    The prior version <strong>remains as audit history</strong>{" "}
                    (checksum, recipe, processing ID) — not as another playable copy.
                  </li>
                  <li>
                    Once the new version is approved and verified, the prior active
                    version becomes <strong>superseded</strong> and its MP3 object is
                    removed after cutover.
                  </li>
                  <li>
                    The <strong>Track ID remains unchanged</strong>{" "}
                    (<EntityRefBadge kind="track" id={trackId} />)
                    — full lineage is preserved.
                  </li>
                </ul>
              )}
            </Alert>
            {errorMessage && errorReasonCode === "source-intake-missing" ? (
              <Alert variant="warning" className="mb-0">
                <strong>Re-upload required.</strong> The original source file for
                this song is no longer retained — production intake originals are
                deleted immediately after a successful finalize (intentional
                cost/security policy). To create a new version, upload the song
                again through the <Link to="/uploader">Song Uploader</Link>; the
                Track and its full version history are preserved.
              </Alert>
            ) : errorMessage ? (
              <Alert variant="danger" className="mb-0">
                {errorMessage}
              </Alert>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={busy || !target}>
          {busy
            ? "Reprocessing…"
            : trackless
              ? "Reprocess (new Processing Job)"
              : `Reprocess (create v${nextVersion})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ---- Resume ------------------------------------------------------------------

/** Human copy for the known awaiting_review park reasons. */
const PARK_REASON_COPY: Record<string, string> = {
  "metadata-low-confidence":
    "Commercial metadata confidence was too low for auto-approval — a reviewer must confirm (or correct) the song details before processing continues.",
  "confidence-review-band":
    "The reconciled metadata confidence landed in the review band — confirm the discovered details (or correct them) before processing continues.",
  "metadata-insufficient":
    "Not enough metadata could be extracted or identified (a title and an artist are required). Provide the missing details, then resume.",
  "provider-unavailable":
    "External identification was unavailable when this song processed. Review the extracted metadata (or correct it), then resume.",
  "catalog-link-unresolved":
    "The pipeline could not resolve the song's catalog links (artist / album / record label). Create the missing catalog records or correct the metadata, then resume.",
};

/**
 * Reviewer-facing names + colors for confidence bands (owner UX 2026-07-17:
 * 🟢 auto-proceed 🟡 review 🔴 low confidence). Keyed by the BAND from the
 * metadata-identification contract, never by hard-coded score ranges — the
 * thresholds are tunable platform config and the API is the source of truth.
 */
const CONFIDENCE_BAND_DISPLAY: Record<string, { label: string; color: string }> = {
  "auto-proceed-eligible": { label: "Auto-approved", color: "#198754" },
  review: { label: "Review Band", color: "#a97a00" },
  "low-confidence-review": { label: "Low Confidence", color: "#dc3545" },
};

/** Friendly names for reconciliation evidence sources (normalized contract —
 * provider names pass through as-is so future providers need no UI change). */
const FIELD_SOURCE_LABELS: Record<string, string> = {
  "intake-hint": "Uploader hint",
  id3: "ID3 tag",
  "filename-parse": "Filename (CFP)",
  operator: "Operator correction",
  audd: "AudD",
  acrcloud: "ACRCloud",
  musicbrainz: "MusicBrainz",
};

function fieldSourceLabel(source: string | undefined | null): string {
  if (!source) return "—";
  return FIELD_SOURCE_LABELS[source] ?? source;
}

function providerDisplayName(provider: string | undefined | null): string {
  if (!provider) return "—";
  return FIELD_SOURCE_LABELS[provider] ?? provider;
}

/**
 * Client-side mirror of the API's resume-point derivation (review-resume.js):
 * audio-processing passed → resume at Catalog Linking; otherwise, if
 * metadata-discovery is resolved → resume at Audio Processing (enriching).
 */
function deriveResumePointLabel(job: ProcessingJob): string {
  const byKey = new Map((job.checkpoints ?? []).map((c) => [c.key, c]));
  const audio = byKey.get("audio-processing");
  const metadata = byKey.get("metadata-discovery");
  if (audio && audio.status === "pass") return "Catalog Linking";
  if (metadata && metadata.status !== "pending") return "Audio Processing";
  return "the first unresolved pipeline step";
}

/**
 * Effective park reason. Metadata-stage parks never write the TOP-LEVEL job
 * reasonCode (pre-existing P3c persistence behavior) — the canonical CMI-3
 * reason lives on the Metadata Identification decision block, so fall back
 * to it (then to the parked checkpoint) before giving up.
 */
export function deriveParkReasonCode(job: ProcessingJob): string {
  if (job.reasonCode) return job.reasonCode;
  const decisionReason = job.metadataIdentification?.decision?.reasonCode;
  if (decisionReason) return decisionReason;
  const parked = (job.checkpoints ?? []).find((c) => c.reasonCode);
  return parked?.reasonCode || "";
}

/**
 * Decision Audit model for a Processing Job — the permanent explanation of
 * why the pipeline decided what it decided at processing time.
 *
 * Values are read from the job's persisted `metadataIdentification` block
 * (including snapshotted thresholds). Never recompute from live platform
 * config: if thresholds change later, old jobs must still show the values
 * that were in effect when they processed.
 *
 * Owner direction 2026-07-21: the UI surface is named "Decision Audit" —
 * the single destination for auditing a song (processing summary, provider
 * evidence, current metadata, corrections, history).
 */
interface ReviewModel {
  selected: {
    title: string;
    artist: string;
    album: string;
    recordLabelName: string;
    releaseYear: string;
    isrc: string;
    mediaSource: string;
  };
  fields: Record<string, ReconciledFieldRecord | undefined>;
  confidenceScore: number | null;
  confidenceBand: string | null;
  autoProceedThreshold: number | null;
  reviewThreshold: number | null;
  providerScore: number | null;
  decisionOutcome: string | null;
  decisionReasonCode: string | null;
  businessSummary: string | null;
  confidenceReasons: string[];
  attempts: IdentificationAttempt[];
  winningProvider: string | null;
  extractionSummary: string | null;
  extractionNotes: string[];
  reportingStatus: string | null;
  reportingReasons: string[];
  operatorCorrections: unknown[];
  warnings: string[];
  identified: boolean;
}

/** Processing-time routing outcome (historical — never re-derived from today's config). */
function processingOutcomeLabel(outcome: string | null | undefined): string {
  if (outcome === "proceed") return "Auto Approved";
  if (outcome === "review") return "Sent for Review";
  if (!outcome) return "—";
  return outcome;
}

function finalDecisionLabel(review: ReviewModel): string {
  const outcome = processingOutcomeLabel(review.decisionOutcome);
  if (review.operatorCorrections.length > 0) {
    return `${outcome} → operator override applied after automation`;
  }
  if (review.decisionOutcome === "proceed") {
    return "Pipeline auto-approved using the snapshotted thresholds on this job";
  }
  if (review.decisionOutcome === "review") {
    return (
      review.businessSummary ||
      "Pipeline sent this job for operator review using the snapshotted thresholds on this job"
    );
  }
  return review.businessSummary || outcome;
}

/** True when the job has Metadata Identification evidence worth auditing. */
export function jobHasConfidenceAudit(job: ProcessingJob | null | undefined): boolean {
  if (!job?.metadataIdentification) return false;
  const phases = job.metadataIdentification.phases;
  if (phases?.confidence?.score != null) return true;
  if (phases?.reconciliation?.selected?.title || phases?.reconciliation?.selected?.artist) {
    return true;
  }
  const attempts = phases?.identification?.attempts;
  return Array.isArray(attempts) && attempts.length > 0;
}

export function deriveReviewModel(job: ProcessingJob | null): ReviewModel | null {
  const mi = job?.metadataIdentification;
  const phases = mi?.phases;
  const selected = phases?.reconciliation?.selected;
  const confidence = phases?.confidence;
  const attempts = Array.isArray(phases?.identification?.attempts)
    ? phases!.identification!.attempts!
    : [];
  // Allow audit when we have a score even if selection is sparse (edge jobs).
  if (
    !selected?.title &&
    !selected?.artist &&
    confidence?.score == null &&
    attempts.length === 0
  ) {
    return null;
  }
  const identified = attempts.some((a) => a?.outcome === "matched");
  const matched = attempts.filter((a) => a?.outcome === "matched");
  const winningProvider =
    matched.length > 0
      ? [...matched].sort(
          (a, b) => (b.score ?? -1) - (a.score ?? -1),
        )[0]?.provider ?? null
      : null;
  const asText = (v: unknown): string => (v == null ? "" : String(v));
  const fields = phases?.reconciliation?.fields ?? {};
  const warnings: string[] = [];
  for (const [name, record] of Object.entries(fields)) {
    if (!record) continue;
    if (record.strongConflict) {
      warnings.push(`Strong metadata conflict on ${name}`);
    } else if (record.conflict) {
      warnings.push(`Metadata difference on ${name}`);
    }
  }
  const rawConfidenceReasons = confidence?.reasons;
  const confidenceReasons = Array.isArray(rawConfidenceReasons)
    ? rawConfidenceReasons.filter((r): r is string => typeof r === "string")
    : [];
  for (const r of confidenceReasons) {
    if (/conflict|ambiguous|low|skip|unavailable/i.test(r)) {
      warnings.push(r);
    }
  }
  const reporting = mi?.reporting ?? null;
  if (reporting?.status && reporting.status !== "ready") {
    warnings.push(`Reporting: ${reporting.status}`);
  }
  const extraction = phases?.extraction;
  const extractionSummary =
    typeof extraction?.summary === "string" ? extraction.summary : null;
  const rawExtractionNotes = extraction?.notes;
  const extractionNotes = Array.isArray(rawExtractionNotes)
    ? rawExtractionNotes.filter((n): n is string => typeof n === "string")
    : [];
  const rawReportingReasons = reporting?.reasons;
  const reportingReasons = Array.isArray(rawReportingReasons)
    ? rawReportingReasons.filter((r): r is string => typeof r === "string")
    : [];
  const rawCorrections = phases?.reconciliation?.corrections;
  const operatorCorrections = Array.isArray(rawCorrections) ? rawCorrections : [];
  return {
    selected: {
      title: asText(selected?.title),
      artist: asText(selected?.artist),
      album: asText(selected?.album),
      recordLabelName: asText(selected?.recordLabelName),
      releaseYear: asText(selected?.releaseYear),
      isrc: asText(selected?.isrc),
      mediaSource: asText(selected?.mediaSource) || asText(job?.mediaSource),
    },
    fields,
    confidenceScore: confidence?.score ?? null,
    confidenceBand: confidence?.band ?? null,
    autoProceedThreshold: confidence?.thresholds?.autoProceed ?? null,
    reviewThreshold: confidence?.thresholds?.review ?? null,
    providerScore: confidence?.providerScore ?? null,
    decisionOutcome: mi?.decision?.outcome ?? null,
    decisionReasonCode: mi?.decision?.reasonCode ?? null,
    businessSummary: mi?.decision?.businessSummary ?? null,
    confidenceReasons,
    attempts,
    winningProvider: winningProvider ? String(winningProvider) : null,
    extractionSummary,
    extractionNotes,
    reportingStatus: reporting?.status ?? null,
    reportingReasons,
    operatorCorrections,
    warnings: Array.from(new Set(warnings)),
    identified,
  };
}

/**
 * Review-first reason line, shown directly under the confidence hero.
 * Owner UX 2026-07-17: lead with KROSSKAST making the decision — providers
 * are just evidence sources.
 */
function parkReasonLine(reasonCode: string, review: ReviewModel | null): string {
  if (!reasonCode && review?.decisionOutcome === "proceed") {
    return "KrossKast auto-approved this song — confidence met the configured auto-proceed threshold. No operator intervention was required.";
  }
  if (reasonCode === "confidence-review-band") {
    return review?.identified
      ? "KrossKast successfully identified this recording, but the confidence falls within the manual review band."
      : "KrossKast reconciled the available metadata, but the confidence falls within the manual review band.";
  }
  if (reasonCode === "metadata-low-confidence") {
    return "KrossKast could not establish sufficient confidence in this metadata — verify or correct the details before continuing.";
  }
  return (
    PARK_REASON_COPY[reasonCode] ||
    (review?.businessSummary
      ? review.businessSummary
      : "This job was parked for manual review before processing could continue.")
  );
}

/** Headline confidence block — the entire reason this screen exists, so it
 * reads like a scoreboard: label / big color-coded score / band. */
function ConfidenceHero({
  review,
  reasonCode,
}: {
  review: ReviewModel;
  reasonCode: string;
}): React.ReactElement {
  const band = review.confidenceBand
    ? CONFIDENCE_BAND_DISPLAY[review.confidenceBand] ?? {
        label: review.confidenceBand,
        color: "#6c757d",
      }
    : null;
  const color = band?.color ?? "#6c757d";
  const effectiveReason = reasonCode || review.decisionReasonCode || "";
  return (
    <div
      className="text-center rounded mb-4 px-3 py-3"
      style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
    >
      <div
        className="text-muted"
        style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1.2 }}
      >
        KrossKast Confidence
      </div>
      <div style={{ fontSize: 48, fontWeight: 700, lineHeight: 1.15, color }}>
        {review.confidenceScore != null ? review.confidenceScore : "—"}
      </div>
      {band && (
        <div style={{ fontSize: 15, fontWeight: 600, color }}>{band.label}</div>
      )}
      <div className="text-muted mt-2 mx-auto" style={{ fontSize: 14, maxWidth: 520 }}>
        {parkReasonLine(effectiveReason, review)}
      </div>
      {effectiveReason ? (
        <div className="mt-1">
          <code style={{ fontSize: 11, color: "#6c757d" }}>{effectiveReason}</code>
        </div>
      ) : null}
    </div>
  );
}

function AuditFact({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div style={{ minWidth: 140, flex: "1 1 140px" }}>
      <div
        className="text-muted"
        style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {label}
      </div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{value}</div>
    </div>
  );
}

/** Shared Decision Audit body (automation + review). Values are historical. */
function ConfidenceAuditBody({
  review,
  reasonCode,
}: {
  review: ReviewModel;
  reasonCode: string;
}): React.ReactElement {
  const conflictWarnings = review.warnings.filter((w) =>
    /conflict|difference/i.test(w),
  );
  const otherWarnings = review.warnings.filter(
    (w) => !/conflict|difference/i.test(w),
  );
  const overridden = review.operatorCorrections.length > 0;

  return (
    <>
      <ConfidenceHero review={review} reasonCode={reasonCode} />

      <div
        className="rounded mb-3 px-3 py-3"
        style={{ background: "#fff", border: "1px solid #e9ecef" }}
      >
        <div
          className="text-muted mb-1"
          style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          Decision Audit
        </div>
        <div className="text-muted mb-3" style={{ fontSize: 12.5 }}>
          Historical record of why the pipeline decided what it decided for this
          job. Thresholds and scores are snapshotted at processing time — they
          do not change if platform configuration is updated later.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px" }}>
          <AuditFact
            label="Final confidence score"
            value={review.confidenceScore != null ? review.confidenceScore : "—"}
          />
          <AuditFact
            label="Processing outcome"
            value={processingOutcomeLabel(review.decisionOutcome)}
          />
          <AuditFact
            label="Auto-approval threshold (at processing)"
            value={
              review.autoProceedThreshold != null
                ? `≥ ${review.autoProceedThreshold}`
                : "—"
            }
          />
          <AuditFact
            label="Review threshold (at processing)"
            value={
              review.reviewThreshold != null ? `≥ ${review.reviewThreshold}` : "—"
            }
          />
          <AuditFact
            label="Winning provider"
            value={providerDisplayName(review.winningProvider)}
          />
          <AuditFact
            label="Manually overridden"
            value={overridden ? "Yes" : "No"}
          />
        </div>
        <div className="mt-3">
          <div
            className="text-muted"
            style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Final decision
          </div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>
            {finalDecisionLabel(review)}
          </div>
          {review.decisionReasonCode ? (
            <div className="mt-1">
              <code style={{ fontSize: 11, color: "#6c757d" }}>
                {review.decisionReasonCode}
              </code>
            </div>
          ) : null}
        </div>
      </div>

      {review.attempts.length > 0 ? (
        <div className="mb-4">
          <div
            className="text-muted mb-2"
            style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            Identification providers that participated
          </div>
          <ul className="mb-0 ps-3">
            {review.attempts.map((a, idx) => (
              <li key={`${a.provider || "provider"}-${idx}`} style={{ fontSize: 14, marginBottom: 6 }}>
                <strong>{providerDisplayName(a.provider)}</strong>
                {" — "}
                {a.outcome || "unknown"}
                {a.score != null ? ` (score ${a.score})` : ""}
                {a.error ? (
                  <span className="text-danger"> — {String(a.error)}</span>
                ) : null}
                {a.normalizedResult?.title || a.normalizedResult?.artist ? (
                  <div className="text-muted" style={{ fontSize: 12.5 }}>
                    Match: {[a.normalizedResult?.title, a.normalizedResult?.artist]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-muted mb-4" style={{ fontSize: 13.5 }}>
          No external identification provider attempts were recorded for this job
          (extraction-only / identification skipped).
          {review.extractionSummary
            ? ` Extraction path: ${review.extractionSummary}.`
            : ""}
        </div>
      )}

      {conflictWarnings.length > 0 ? (
        <Alert variant="warning" className="mb-3">
          <strong>Conflicts</strong>
          <ul className="mb-0 mt-2">
            {conflictWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {otherWarnings.length > 0 ? (
        <Alert variant="warning" className="mb-3">
          <strong>Warnings</strong>
          <ul className="mb-0 mt-2">
            {otherWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {overridden ? (
        <Alert variant="info" className="mb-3">
          <strong>Operator override after automation</strong> —{" "}
          {review.operatorCorrections.length} correction
          {review.operatorCorrections.length === 1 ? "" : "s"} recorded on this
          job. The automated selection was superseded and remains audited.
        </Alert>
      ) : (
        <div className="text-muted mb-3" style={{ fontSize: 13 }}>
          Metadata was <strong>not</strong> manually overridden after automation.
        </div>
      )}

      <div className="px-1 mb-2">
        <div
          className="text-muted mb-2"
          style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          Final selected metadata · field-by-field source selection
        </div>
        <ReviewFieldRow label="Title" value={review.selected.title} record={review.fields.title} />
        <ReviewFieldRow label="Artist" value={review.selected.artist} record={review.fields.artist} />
        <ReviewFieldRow label="Album" value={review.selected.album} record={review.fields.album} />
        <ReviewFieldRow
          label="Record Label"
          value={review.selected.recordLabelName}
          record={review.fields.recordLabelName}
        />
        <ReviewFieldRow
          label="Release Year"
          value={review.selected.releaseYear}
          record={review.fields.releaseYear}
        />
        <ReviewFieldRow
          label="Source"
          value={review.selected.mediaSource}
          record={review.fields.mediaSource}
          emptyText="Not recorded"
        />
        <ReviewFieldRow
          label="ISRC"
          value={review.selected.isrc}
          record={review.fields.isrc}
          emptyText="Not available"
          emptyNote="Reporting incomplete only — does not block approval or playback."
        />
      </div>

      {review.confidenceReasons.length > 0 ? (
        <div className="text-muted mb-2" style={{ fontSize: 12.5 }}>
          Score factors (at processing): {review.confidenceReasons.join(" · ")}
        </div>
      ) : null}
    </>
  );
}

/** One read-only field of the review list: label / value / evidence source. */
function ReviewFieldRow({
  label,
  value,
  record,
  emptyText,
  emptyNote,
}: {
  label: string;
  value: string;
  record: ReconciledFieldRecord | undefined;
  emptyText?: string;
  emptyNote?: string;
}): React.ReactElement {
  const candidates = Array.isArray(record?.candidates)
    ? (record!.candidates as Array<{
        source?: string;
        value?: unknown;
        providerConfidence?: number | null;
        krosskastConfidence?: number | null;
      }>)
    : [];
  return (
    <div className="mb-3">
      <div
        className="text-muted"
        style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.6 }}
      >
        {label}
      </div>
      <div className="fw-semibold" style={{ fontSize: 17, lineHeight: 1.35 }}>
        {value || <span className="text-muted fw-normal">{emptyText || "—"}</span>}
      </div>
      {!value && emptyNote ? (
        <div className="text-muted" style={{ fontSize: 12.5 }}>
          {emptyNote}
        </div>
      ) : null}
      {record?.source ? (
        <div className="text-muted" style={{ fontSize: 12.5 }}>
          Winning source: {fieldSourceLabel(record.source)}
          {record.krosskastConfidence != null
            ? ` · KK confidence ${record.krosskastConfidence}`
            : ""}
          {record.providerConfidence != null
            ? ` · provider ${record.providerConfidence}`
            : ""}
          {record.conflict ? " · difference across sources" : ""}
        </div>
      ) : null}
      {candidates.length > 1 ? (
        <ul className="mb-0 mt-1 ps-3 text-muted" style={{ fontSize: 12 }}>
          {candidates.map((c, i) => (
            <li key={`${c.source}-${i}`}>
              {fieldSourceLabel(c.source)}: {c.value == null ? "—" : String(c.value)}
              {c.providerConfidence != null ? ` (provider ${c.providerConfidence})` : ""}
              {c.krosskastConfidence != null ? ` (KK ${c.krosskastConfidence})` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Parsed shape of one append-only Decision Audit correction record. */
interface CorrectionRecordView {
  field: string;
  correctedAt: string | null;
  correctedBySub: string | null;
  reason: string | null;
  correctedValue: string;
  previousValue: string;
}

function parseCorrectionRecords(raw: unknown[]): CorrectionRecordView[] {
  return raw
    .filter((c): c is Record<string, unknown> => !!c && typeof c === "object")
    .map((c) => ({
      field: typeof c.field === "string" ? c.field : "unknown",
      correctedAt: typeof c.correctedAt === "string" ? c.correctedAt : null,
      correctedBySub: typeof c.correctedBySub === "string" ? c.correctedBySub : null,
      reason: typeof c.reason === "string" ? c.reason : null,
      correctedValue: c.correctedValue == null ? "—" : String(c.correctedValue),
      previousValue:
        c.previousSelectedValue == null ? "—" : String(c.previousSelectedValue),
    }));
}

/** MDC-1 — permanent correction history under the Decision Audit. The
 * original automated decision above is never rewritten; this section shows
 * what humans changed afterwards, by whom, when, and why. */
function CorrectionHistory({
  corrections,
  resolveActor,
}: {
  corrections: unknown[];
  resolveActor: (sub: string | null) => string;
}): React.ReactElement | null {
  const records = parseCorrectionRecords(corrections);
  if (records.length === 0) return null;
  return (
    <div
      className="rounded mb-3 px-3 py-3"
      style={{ background: "#fff", border: "1px solid #e9ecef" }}
    >
      <div
        className="text-muted mb-2"
        style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.6 }}
      >
        Metadata correction history
      </div>
      {records.map((r, i) => (
        <div key={`${r.field}-${i}`} className="mb-2" style={{ fontSize: 13.5 }}>
          <strong style={{ textTransform: "capitalize" }}>{r.field}</strong>
          {": "}
          <span className="text-muted">{r.previousValue}</span>
          {" → "}
          <span className="fw-semibold">{r.correctedValue}</span>
          <div className="text-muted" style={{ fontSize: 12.5 }}>
            Corrected by {resolveActor(r.correctedBySub)}
            {r.correctedAt ? ` on ${formatDateTime(r.correctedAt)}` : ""}
            {r.reason ? ` — “${r.reason}”` : ""}
          </div>
        </div>
      ))}
      <div className="text-muted" style={{ fontSize: 12 }}>
        The automated Decision Audit above is permanent — corrections append
        to history; they never rewrite the original decision or evidence.
      </div>
    </div>
  );
}

/** States in which post-approval correction is offered (mirrors the API:
 * terminal states + awaiting_review; mid-pipeline jobs are blocked). */
const CORRECTABLE_STATES = new Set([
  "approved",
  "not_approved",
  "failed",
  "awaiting_review",
]);

const CORRECTION_UNRESOLVED_COPY: Record<string, string> = {
  "artist-ambiguous":
    "Artist: more than one active Artist has this exact name — the link was NOT changed. Resolve the duplicate in the Artist catalog, then retry.",
  "record-label-ambiguous":
    "Record Label: more than one active Record Label has this exact name — the link was NOT changed. Resolve the duplicate, then retry.",
  "album-ambiguous":
    "Album: more than one active Album matches this title for the artist — the link was NOT changed. Resolve the duplicate, then retry.",
};

/**
 * Decision Audit modal — the single destination for auditing a song (owner
 * direction 2026-07-21). Available for auto-approved and parked jobs alike.
 * Reads only the job's persisted Metadata Identification snapshot — never
 * live threshold config.
 *
 * MDC-1 — opens READ-ONLY; "Correct Metadata" switches to a prefilled
 * correction form. Corrections update the canonical Track (with catalog
 * relinking) and append to the job's permanent correction history. The
 * original confidence score, provider evidence, and processing decision are
 * IMMUTABLE — corrections never rewrite them.
 */
export function ConfidenceAuditModal({
  show,
  job,
  onClose,
  onOpenResumeReview,
  resolveActor,
}: {
  show: boolean;
  job: ProcessingJob | null;
  onClose: () => void;
  /** When the job is parked, offer a path into the actionable Resume Review. */
  onOpenResumeReview?: () => void;
  /** Human-identity resolver (uploaderDisplay directory) for correction history. */
  resolveActor?: (sub: string | null) => string;
}): React.ReactElement {
  const review = useMemo(() => deriveReviewModel(job), [job]);
  const reasonCode = job ? deriveParkReasonCode(job) : "";
  const canResume = job?.state === "awaiting_review";
  const canEdit = !!review && !!job && CORRECTABLE_STATES.has(job.state);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [artistName, setArtistName] = useState("");
  const [album, setAlbum] = useState("");
  const [recordLabelName, setRecordLabelName] = useState("");
  const [releaseYear, setReleaseYear] = useState("");
  const [isrc, setIsrc] = useState("");
  const [mediaSource, setMediaSource] = useState("");
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [unresolvedNotes, setUnresolvedNotes] = useState<string[]>([]);

  const [correctMetadata, { isLoading: saving }] = useCorrectJobMetadataMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (show) {
      // Read-only first (owner UX): edit mode starts CLOSED and prefilled
      // with the final selected metadata, so corrections are tweaks.
      setEditing(false);
      setTitle(review?.selected.title ?? "");
      setArtistName(review?.selected.artist ?? "");
      setAlbum(review?.selected.album ?? "");
      setRecordLabelName(review?.selected.recordLabelName ?? "");
      setReleaseYear(review?.selected.releaseYear ?? "");
      setIsrc(review?.selected.isrc ?? "");
      setMediaSource(review?.selected.mediaSource ?? "");
      setReason("");
      setErrorMessage("");
      setUnresolvedNotes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, job?.jobId, job?.version]);

  const buildFields = (): CorrectJobMetadataFields | { error: string } => {
    if (!review) return { error: "No Decision Audit evidence to correct." };
    const fields: CorrectJobMetadataFields = {};
    const changed = (input: string, current: string) =>
      input.trim() !== current.trim();

    if (title.trim() && changed(title, review.selected.title)) {
      fields.title = title.trim();
    }
    if (artistName.trim() && changed(artistName, review.selected.artist)) {
      fields.artistName = artistName.trim();
    }
    if (recordLabelName.trim() && changed(recordLabelName, review.selected.recordLabelName)) {
      fields.recordLabelName = recordLabelName.trim();
    }
    if (changed(album, review.selected.album)) {
      fields.album = album.trim() ? album.trim() : null; // blank clears
    }
    if (changed(releaseYear, review.selected.releaseYear)) {
      if (!releaseYear.trim()) {
        fields.releaseYear = null;
      } else {
        const y = Number.parseInt(releaseYear.trim(), 10);
        if (!Number.isInteger(y) || y < 1900 || y > 2100) {
          return { error: "Release Year must be a year between 1900 and 2100 (or blank to clear)." };
        }
        fields.releaseYear = y;
      }
    }
    if (changed(isrc, review.selected.isrc)) {
      fields.isrc = isrc.trim() ? isrc.trim() : null; // blank clears — never blocks
    }
    if (changed(mediaSource, review.selected.mediaSource)) {
      if (!mediaSource.trim() || !isMediaSource(mediaSource.trim())) {
        return {
          error: `Source is required (one of: ${MEDIA_SOURCE_OPTIONS.join(", ")}).`,
        };
      }
      fields.mediaSource = mediaSource.trim();
    }

    if (Object.keys(fields).length === 0) {
      return { error: "Nothing changed — adjust at least one field before saving." };
    }
    return fields;
  };

  const submitCorrection = async () => {
    if (!job) return;
    setErrorMessage("");
    if (!reason.trim()) {
      setErrorMessage("A correction reason is required — it becomes part of the permanent audit record.");
      return;
    }
    const fields = buildFields();
    if ("error" in fields) {
      setErrorMessage(fields.error);
      return;
    }
    try {
      const res = await correctMetadata({
        jobId: job.jobId,
        reason: reason.trim(),
        fields,
      }).unwrap();
      const notes = (res.unresolved || []).map(
        (u) => CORRECTION_UNRESOLVED_COPY[u] || `Unresolved: ${u}`,
      );
      setUnresolvedNotes(notes);
      // The correction also updated the canonical Track (different RTK
      // slice), so the Song Library must refetch to show corrected metadata.
      if (res.trackId) {
        dispatch(tracksApi.util.invalidateTags([{ type: "Tracks", id: "LIST" }]));
      }
      if (notes.length > 0) {
        toast(
          "Correction partially applied — some catalog links need attention.",
          { icon: "⚠️" },
        );
      } else {
        toast.success("Metadata corrected — the change is audited and the song record updated.");
      }
      setEditing(false);
      setReason("");
    } catch (err) {
      setErrorMessage(
        extractProcessingErrorMessage(err) || "Failed to save the correction.",
      );
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Decision Audit</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {job && (
          <>
            <div className="mb-3">
              <JobRef jobId={job.jobId} prefix="Job" />
            </div>
            {unresolvedNotes.length > 0 ? (
              <Alert variant="warning" className="mb-3">
                <strong>Correction partially applied</strong>
                <ul className="mb-0 mt-2 ps-3">
                  {unresolvedNotes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
            {review ? (
              <>
                {!editing && (
                  <CorrectionHistory
                    corrections={review.operatorCorrections}
                    resolveActor={resolveActor || ((sub) => sub || "unknown")}
                  />
                )}
                {editing ? (
                  <>
                    <Alert variant="info" className="mb-3">
                      <strong>Correcting metadata for this song.</strong> The
                      automated Decision Audit is kept permanently; your change
                      is appended to the correction history with your name,
                      the date, and your reason. Artist / Record Label / Album
                      are relinked by name (created if they don't exist yet).
                    </Alert>
                    <Form>
                      <Form.Group className="mb-2">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={title}
                          maxLength={300}
                          onChange={(e) => setTitle(e.target.value)}
                          disabled={saving}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Primary Artist</Form.Label>
                        <Form.Control
                          value={artistName}
                          maxLength={300}
                          onChange={(e) => setArtistName(e.target.value)}
                          disabled={saving}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Album</Form.Label>
                        <Form.Control
                          value={album}
                          maxLength={300}
                          onChange={(e) => setAlbum(e.target.value)}
                          placeholder="Leave blank for a single (no album)"
                          disabled={saving}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Record Label</Form.Label>
                        <Form.Control
                          value={recordLabelName}
                          maxLength={300}
                          onChange={(e) => setRecordLabelName(e.target.value)}
                          disabled={saving}
                        />
                      </Form.Group>
                      <div className="d-flex gap-2">
                        <Form.Group className="mb-2 flex-fill">
                          <Form.Label>Release Year</Form.Label>
                          <Form.Control
                            value={releaseYear}
                            maxLength={4}
                            inputMode="numeric"
                            onChange={(e) => setReleaseYear(e.target.value)}
                            placeholder="Blank to clear"
                            disabled={saving}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2 flex-fill">
                          <Form.Label>ISRC</Form.Label>
                          <Form.Control
                            value={isrc}
                            maxLength={15}
                            onChange={(e) => setIsrc(e.target.value)}
                            placeholder="Optional — never blocks"
                            disabled={saving}
                          />
                        </Form.Group>
                      </div>
                      <Form.Group className="mb-2">
                        <Form.Label>Source *</Form.Label>
                        <Form.Select
                          value={mediaSource}
                          onChange={(e) => setMediaSource(e.target.value)}
                          disabled={saving}
                          aria-required="true"
                        >
                          <option value="">Select source…</option>
                          {MEDIA_SOURCE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>
                          Reason for correction <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={reason}
                          maxLength={500}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Recorded permanently in the audit trail"
                          disabled={saving}
                        />
                      </Form.Group>
                    </Form>
                    {errorMessage && (
                      <Alert variant="danger" className="mb-0 mt-2">
                        {errorMessage}
                      </Alert>
                    )}
                  </>
                ) : (
                  <ConfidenceAuditBody review={review} reasonCode={reasonCode} />
                )}
              </>
            ) : (
              <Alert variant="secondary" className="mb-0">
                This Song Processing job has no Decision Audit evidence yet (Metadata
                Identification was not recorded, or the job predates the audit
                contract).
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {editing ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setErrorMessage("");
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitCorrection}
              disabled={saving}
              data-testid="save-correction"
            >
              {saving ? "Saving…" : "Save Correction"}
            </Button>
          </>
        ) : (
          <>
            {canEdit ? (
              <Button
                variant="outline-dark"
                onClick={() => setEditing(true)}
                data-testid="edit-metadata"
              >
                Correct Metadata
              </Button>
            ) : null}
            {canResume && onOpenResumeReview ? (
              <Button
                variant="warning"
                onClick={() => {
                  onClose();
                  onOpenResumeReview();
                }}
              >
                Open Resume Review
              </Button>
            ) : null}
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export function ResumeReviewModal({
  show,
  job,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  show: boolean;
  job: ProcessingJob | null;
  busy: boolean;
  errorMessage: string;
  onCancel: () => void;
  onConfirm: (corrections: ResumeJobRequest) => void;
}): React.ReactElement {
  const review = useMemo(() => deriveReviewModel(job), [job]);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [artistName, setArtistName] = useState("");
  const [album, setAlbum] = useState("");

  useEffect(() => {
    if (show) {
      // Review-first: edit mode starts CLOSED and prefilled with the
      // reconciled selection, so corrections are tweaks, never retyping.
      setEditing(false);
      setTitle(review?.selected.title ?? "");
      setArtistName(review?.selected.artist ?? "");
      setAlbum(review?.selected.album ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, job?.jobId]);

  const resumePoint = useMemo(
    () => (job ? deriveResumePointLabel(job) : ""),
    [job],
  );
  const reasonCode = job ? deriveParkReasonCode(job) : "";

  const acceptAndContinue = () => onConfirm({});

  const submitWithCorrections = () => {
    // Only send fields that actually differ from the reconciled selection —
    // unchanged values must not create no-op operator-correction history.
    const corrections: ResumeJobRequest = {};
    const changed = (input: string, current: string) =>
      input.trim() && input.trim() !== current.trim();
    if (review) {
      if (changed(title, review.selected.title)) corrections.title = title.trim();
      if (changed(artistName, review.selected.artist)) corrections.artistName = artistName.trim();
      if (changed(album, review.selected.album)) corrections.album = album.trim();
    } else {
      if (title.trim()) corrections.title = title.trim();
      if (artistName.trim()) corrections.artistName = artistName.trim();
      if (album.trim()) corrections.album = album.trim();
    }
    onConfirm(corrections);
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Resume review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {job && (
          <>
            <div className="mb-3">
              <JobRef jobId={job.jobId} prefix="Job" />
            </div>

            {review ? (
              <>
                <ConfidenceAuditBody review={review} reasonCode={reasonCode} />
                <div className="mb-3">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => setEditing((e) => !e)}
                    disabled={busy}
                  >
                    {editing ? "Cancel override" : "Override Metadata"}
                  </Button>
                  {!editing && (
                    <span className="text-muted ms-2" style={{ fontSize: 13 }}>
                      Looks correct? Accept &amp; Continue. Overrides are the
                      exception, not the workflow — and are audited when used.
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <Alert variant="warning" className="mb-3">
                  <div className="mb-1">
                    <strong>Why this job parked:</strong>{" "}
                    {reasonCode ? <code>{reasonCode}</code> : "—"}
                  </div>
                  <div>{parkReasonLine(reasonCode, null)}</div>
                </Alert>
                <p className="text-muted mb-2">
                  Optional corrections — leave blank to resume with the discovered
                  metadata unchanged. Corrections feed catalog re-linking.
                </p>
              </>
            )}

            {(editing || !review) && (
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    value={title}
                    maxLength={300}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Corrected song title (optional)"
                    disabled={busy}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Artist</Form.Label>
                  <Form.Control
                    value={artistName}
                    maxLength={300}
                    onChange={(e) => setArtistName(e.target.value)}
                    placeholder="Corrected artist name (optional)"
                    disabled={busy}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Album</Form.Label>
                  <Form.Control
                    value={album}
                    maxLength={300}
                    onChange={(e) => setAlbum(e.target.value)}
                    placeholder="Corrected album (optional)"
                    disabled={busy}
                  />
                </Form.Group>
              </Form>
            )}

            <Alert variant="info" className="mb-0 mt-3">
              <strong>Where it resumes:</strong> the pipeline will resume at{" "}
              <strong>{resumePoint}</strong>. Already-completed steps are skipped;
              the job re-drives forward from that point.
            </Alert>
            {errorMessage && (
              <Alert variant="danger" className="mb-0 mt-2">
                {errorMessage}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {review && !editing ? (
          <Button variant="primary" onClick={acceptAndContinue} disabled={busy || !job}>
            {busy ? "Resuming…" : "Accept & Continue"}
          </Button>
        ) : (
          <Button variant="primary" onClick={submitWithCorrections} disabled={busy || !job}>
            {busy ? "Resuming…" : review ? "Resume with Overrides" : "Resume Processing"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ---- Revoke ------------------------------------------------------------------

const REVOKE_CONFIRM_WORD = "REVOKE";

export function RevokeArtifactModal({
  show,
  artifact,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  show: boolean;
  artifact: ApprovedArtifactVersion | null;
  busy: boolean;
  errorMessage: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): React.ReactElement {
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (show) {
      setConfirmText("");
      setReason("");
    }
  }, [show, artifact?.artifactVersionId]);

  const formValid =
    confirmText === REVOKE_CONFIRM_WORD && reason.trim().length > 0;

  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Revoke artifact version</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {artifact && (
          <>
            <p className="mb-1">
              Revoke <strong>v{artifact.version}</strong>{" "}
              {artifact.status === "active" ? "(currently active)" : `(${artifact.status})`}?
            </p>
            <div className="mb-3" style={MONO}>
              {artifact.artifactVersionId}
            </div>
            <Alert variant="danger" className="mb-3">
              <strong>This action affects live playback.</strong>
              <ul className="mb-0 mt-2 ps-3">
                <li>
                  The Track becomes <strong>not playable</strong>
                  {artifact.status === "active"
                    ? " immediately (this is the active playback version)."
                    : " if this version is the live playback pointer."}
                </li>
                <li>
                  The current Approved audio object is <strong>deleted</strong>.
                </li>
                <li>
                  A <strong>forensic copy is retained</strong> as a noncurrent S3
                  version — but it is no longer playable.
                </li>
                <li>
                  Recovery requires a <strong>reprocess</strong> (or a new upload in
                  production), which creates the next version.
                </li>
              </ul>
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>
                Type <strong>{REVOKE_CONFIRM_WORD}</strong> to confirm
              </Form.Label>
              <Form.Control
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={REVOKE_CONFIRM_WORD}
                disabled={busy}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Reason (recorded in the audit trail)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this artifact version being revoked?"
                disabled={busy}
              />
            </Form.Group>
            {errorMessage && (
              <Alert variant="danger" className="mb-0 mt-2">
                {errorMessage}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => onConfirm(reason.trim())}
          disabled={busy || !artifact || !formValid}
        >
          {busy ? "Revoking…" : "Revoke Artifact Version"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ---- PJL-3 lifecycle management (archive / restore / hard delete) -------------

/** "18 Archived Jobs" / "1 Job" — owner UX #8 (2026-07-17): every bulk action
 * label carries the exact count so operators always know the blast radius. */
function jobsPhrase(count: number, adjective?: string): string {
  const noun = count === 1 ? "Job" : "Jobs";
  return adjective ? `${count} ${adjective} ${noun}` : `${count} ${noun}`;
}

export function ArchiveJobsModal({
  show,
  jobIds,
  jobCount,
  awaitingReviewCount,
  stalledCount = 0,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  show: boolean;
  jobIds: string[];
  /** Overrides jobIds.length for "select all matching" (matched total from
   * count API). Confirm copy should note dependency rules still apply and
   * large ops continue via durable BulkOp on the server. */
  jobCount?: number;
  /** How many selected jobs are parked awaiting_review (will be terminalized). */
  awaitingReviewCount: number;
  /** Operator Guidance (owner 2026-07-19) — how many selected jobs are
   * STALLED in-flight jobs (terminalized with stalled-archived-by-operator). */
  stalledCount?: number;
  busy: boolean;
  errorMessage: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (show) setReason("");
  }, [show]);
  const count = jobCount ?? jobIds.length;
  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Archive {jobsPhrase(count)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {count === 1 && (
          <div className="mb-3">
            <JobRef jobId={jobIds[0]} prefix="Job" />
          </div>
        )}
        <Alert variant="info" className="mb-3">
          <ul className="mb-0 ps-3">
            <li>
              Archived jobs are <strong>removed from the Active list</strong> and
              move to the <strong>Archived</strong> tab.
            </li>
            <li>
              All history, evidence, and audit records are <strong>kept</strong> —
              archive is fully reversible with Restore.
            </li>
            <li data-testid="archive-duplicate-note">
              Archived jobs <strong>still participate in duplicate detection</strong>
              — an exact re-upload of the same file stays blocked. Only permanent
              deletion releases the file for a fresh upload.
            </li>
            {jobCount != null && jobCount > 1 ? (
              <li>
                Dependency and lifecycle rules still apply per job. Large
                selections run as a durable background operation you can refresh
                away from — progress continues on the server.
              </li>
            ) : null}
          </ul>
        </Alert>
        {awaitingReviewCount > 0 && (
          <Alert variant="warning" className="mb-3">
            {jobsPhrase(awaitingReviewCount)}{" "}
            {awaitingReviewCount === 1 ? "is" : "are"} parked awaiting review.
            Archiving finalizes {awaitingReviewCount === 1 ? "it" : "them"} as{" "}
            <strong>Not Approved</strong> (reason: <code>archived-by-operator</code>)
            — archived jobs never remain live.
          </Alert>
        )}
        {stalledCount > 0 && (
          <Alert variant="warning" className="mb-3" data-testid="archive-stalled-note">
            {jobsPhrase(stalledCount, "stalled")}{" "}
            {stalledCount === 1 ? "has" : "have"} stopped progressing and will
            not resume on {stalledCount === 1 ? "its" : "their"} own. Archiving
            finalizes {stalledCount === 1 ? "it" : "them"} as{" "}
            <strong>Not Approved</strong> (reason:{" "}
            <code>stalled-archived-by-operator</code>). The uploaded file and
            full history stay retained. To run the exact same file again,
            permanently delete the archived job first — until then duplicate
            detection keeps blocking that file.
          </Alert>
        )}
        <Form.Group className="mb-2">
          <Form.Label>Reason (optional — recorded in the audit trail)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. QA cleanup"
            disabled={busy}
          />
        </Form.Group>
        {errorMessage && (
          <Alert variant="danger" className="mb-0 mt-2">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => onConfirm(reason.trim())}
          disabled={busy || count === 0}
        >
          {busy ? "Archiving…" : `Archive ${jobsPhrase(count)}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function RestoreJobsModal({
  show,
  jobIds,
  jobCount,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  show: boolean;
  jobIds: string[];
  /** PJL-3.2 — overrides jobIds.length for "select all matching results". */
  jobCount?: number;
  busy: boolean;
  errorMessage: string;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const count = jobCount ?? jobIds.length;
  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Restore {jobsPhrase(count, "Archived")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {count === 1 && (
          <div className="mb-3">
            <JobRef jobId={jobIds[0]} prefix="Job" />
          </div>
        )}
        <p className="mb-0">
          Restored jobs return to the <strong>Active</strong> list. Pipeline
          state, decision, and history are untouched — the archive record itself
          is retained for the audit trail.
        </p>
        {errorMessage && (
          <Alert variant="danger" className="mb-0 mt-3">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={busy || count === 0}>
          {busy ? "Restoring…" : `Restore ${jobsPhrase(count)}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const DELETE_CONFIRM_WORD = "DELETE";
/** Wait for a typing pause before freezing elevated delete scope. */
const ELEVATED_DELETE_PREPARE_DEBOUNCE_MS = 1000;

export type ElevatedDeleteConfirm = {
  reason: string;
};

export function DeleteJobsModal({
  show,
  jobIds,
  jobCount,
  busy,
  errorMessage,
  deletionScope,
  forceElevated = false,
  environmentName = "staging",
  preparingScope = false,
  onCancel,
  onConfirm,
  onPrepareScope,
  onConfirmElevated,
}: {
  show: boolean;
  jobIds: string[];
  /** PJL-3.2 — overrides jobIds.length for "select all matching results". */
  jobCount?: number;
  busy: boolean;
  errorMessage: string;
  /**
   * Server-computed deletion scope after internal preflight (not a separate
   * user-facing preview lifecycle step).
   */
  deletionScope?: import("./processingTypes").DestructiveDeleteDryRunSummary | null;
  /** Select-all / large selections require elevated confirmation. */
  forceElevated?: boolean;
  /** Display-only stage name in the scope summary (not a typed field). */
  environmentName?: string;
  /** True while internal preflight is computing the frozen eligible scope. */
  preparingScope?: boolean;
  onCancel: () => void;
  /** Normal path (≤25): reason + DELETE. */
  onConfirm: (reason: string) => void;
  /** Elevated path: prepare frozen scope after DELETE + reason (debounced). */
  onPrepareScope?: (reason: string) => void;
  /** Elevated path: execute permanent delete for the frozen scope. */
  onConfirmElevated?: (payload: ElevatedDeleteConfirm) => void;
}): React.ReactElement {
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const prepareRequestedRef = useRef(false);
  useEffect(() => {
    if (show) {
      setConfirmText("");
      setReason("");
      prepareRequestedRef.current = false;
    }
  }, [show]);
  useEffect(() => {
    // Allow retry after a failed prepare.
    if (errorMessage) prepareRequestedRef.current = false;
  }, [errorMessage]);
  const count = jobCount ?? jobIds.length;
  const elevatedRequired = forceElevated || count > 25 || !!deletionScope;
  const eligible = Number(
    deletionScope?.approvedEligibleCount ??
      deletionScope?.totals.eligibleStandalone ??
      0,
  );
  const protectedCount = Number(deletionScope?.totals.governedProtected ?? 0);
  const matchedCount = deletionScope
    ? Number(deletionScope.totals.eligibleStandalone || 0) +
      Number(deletionScope.totals.governedProtected || 0) +
      Number(deletionScope.totals.ineligible || 0)
    : count;
  const elevatedReady =
    !!deletionScope &&
    confirmText === DELETE_CONFIRM_WORD &&
    reason.trim().length > 0 &&
    eligible > 0;

  // Auto-prepare frozen ID scope after DELETE + a non-empty reason — but wait
  // for the operator to pause typing so the first letter of "staging cleanup"
  // does not lock the form mid-sentence. Inputs stay editable while preparing;
  // only the Permanently Delete CTA waits on the frozen scope.
  useEffect(() => {
    if (!show || !elevatedRequired || deletionScope || !onPrepareScope) return;
    if (preparingScope || busy) return;
    if (confirmText !== DELETE_CONFIRM_WORD || reason.trim().length === 0) return;
    if (prepareRequestedRef.current) return;

    const timer = window.setTimeout(() => {
      if (prepareRequestedRef.current) return;
      prepareRequestedRef.current = true;
      onPrepareScope(reason.trim());
    }, ELEVATED_DELETE_PREPARE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    show,
    elevatedRequired,
    deletionScope,
    onPrepareScope,
    preparingScope,
    busy,
    confirmText,
    reason,
  ]);

  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>
          {deletionScope
            ? `Permanently Delete ${eligible.toLocaleString()} Job${eligible === 1 ? "" : "s"}`
            : `Delete ${jobsPhrase(count, "Archived")}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {count === 1 && !deletionScope && (
          <div className="mb-3">
            <JobRef jobId={jobIds[0]} prefix="Job" />
          </div>
        )}
        <Alert variant="danger" className="mb-3">
          <strong>
            Selected / matched: {matchedCount.toLocaleString()} job
            {matchedCount === 1 ? "" : "s"}.
          </strong>
          <ul className="mb-0 mt-2 ps-3">
            <li>
              Song Processing history for deleted jobs is{" "}
              <strong>permanently removed</strong> from Active/Archived lists and
              cannot be restored from this UI.
            </li>
            <li>
              Append-only audit tombstones are retained; intake/quarantine objects
              for deleted jobs are removed.
            </li>
            <li data-testid="delete-duplicate-release-note">
              Permanent deletion <strong>releases duplicate detection</strong> for
              each deleted job&apos;s exact file — the same file can then be
              uploaded again as a genuinely new Song Processing job.
            </li>
            <li>
              Jobs that belong to an <strong>active or archived song</strong> are
              protected and must be removed via Song Library permanent deletion.
            </li>
            {elevatedRequired && (
              <li>
                Lifecycle: Archive → Permanent Delete. Confirm with{" "}
                <strong>DELETE</strong> and a reason. The server freezes eligible
                IDs before execution.
              </li>
            )}
          </ul>
        </Alert>

        {preparingScope && !deletionScope && (
          <Alert variant="info" className="mb-3">
            Preparing deletion scope… You can keep editing DELETE and the reason.
          </Alert>
        )}

        {deletionScope && (
          <Alert variant="warning" className="mb-3">
            <div>
              <strong>Deletion scope</strong> (operation{" "}
              <code>{deletionScope.operationId}</code>)
            </div>
            <ul className="mb-0 mt-2 ps-3">
              <li>
                Environment: <code>{environmentName}</code>
              </li>
              <li>Eligible standalone: {eligible.toLocaleString()}</li>
              <li>
                Protected (governed Song lineage): {protectedCount.toLocaleString()}
              </li>
              <li>
                Ineligible:{" "}
                {Number(deletionScope.totals.ineligible || 0).toLocaleString()}
              </li>
              <li>
                Track-linked / trackless: {deletionScope.totals.trackLinked} /{" "}
                {deletionScope.totals.trackless}
              </li>
              <li>Estimated chunks: {deletionScope.estimatedChunks}</li>
            </ul>
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            Type <strong>{DELETE_CONFIRM_WORD}</strong> to confirm
          </Form.Label>
          <Form.Control
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={DELETE_CONFIRM_WORD}
            disabled={busy}
            autoComplete="off"
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Reason (required — recorded in the deletion record)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. staging cleanup, bad upload"
            disabled={busy}
          />
        </Form.Group>

        {errorMessage && (
          <Alert variant="danger" className="mb-0 mt-2">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {elevatedRequired ? (
          <Button
            variant="danger"
            onClick={() =>
              deletionScope &&
              onConfirmElevated?.({
                reason: reason.trim(),
              })
            }
            disabled={busy || preparingScope || !elevatedReady}
          >
            {busy
              ? "Deleting…"
              : preparingScope
                ? "Preparing…"
                : `Permanently Delete ${eligible.toLocaleString()} Job${eligible === 1 ? "" : "s"}`}
          </Button>
        ) : (
          <Button
            variant="danger"
            onClick={() => onConfirm(reason.trim())}
            disabled={
              busy ||
              count === 0 ||
              confirmText !== DELETE_CONFIRM_WORD ||
              reason.trim().length === 0
            }
          >
            {busy ? "Deleting…" : `Permanently Delete ${jobsPhrase(count, "Archived")}`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ---- PJL-3.2 bulk batching progress --------------------------------------------

/** State of one bulk run. Large selections use the durable BulkOp API
 * (server worker); progress is polled. Small single-id actions may still
 * use direct endpoints; multi-id legacy chunking must not be used for
 * query-wide select-all. */
export interface BulkRunProgress {
  verb: "Archiving" | "Restoring" | "Deleting" | "Reprocessing";
  /** "collecting" / preparing = BulkOp starting; "running" = work in flight. */
  phase: "collecting" | "running";
  totalJobs: number;
  batch: number;
  totalBatches: number;
  /** Jobs whose batch has finished (any outcome). */
  completed: number;
  succeeded: number;
  skipped: number;
  failed: number;
  /**
   * When true, progress is owned by a server BulkOp — never show the legacy
   * "keep this page open" / "batches of 25" client-orchestration copy.
   */
  durable?: boolean;
}

export function BulkProgressModal({
  progress,
  onContinueInBackground,
}: {
  progress: BulkRunProgress | null;
  /** Durable BulkOps only — hide the modal; server work keeps running. */
  onContinueInBackground?: () => void;
}): React.ReactElement {
  const pct =
    progress && progress.phase === "running" && progress.totalJobs > 0
      ? Math.round((progress.completed / progress.totalJobs) * 100)
      : 0;
  const canBackground = !!progress?.durable && !!onContinueInBackground;
  return (
    // Legacy (non-durable) client batching stays non-dismissible. Durable
    // BulkOps are server-owned — offer "Continue in background" so operators
    // are not trapped on this page for a 10–30 minute delete.
    <Modal
      show={progress != null}
      backdrop={canBackground ? true : "static"}
      keyboard={canBackground}
      onHide={canBackground ? onContinueInBackground : undefined}
      {...SAFE_MODAL_LAYOUT}
    >
      <Modal.Header closeButton={canBackground}>
        <Modal.Title>{progress ? `${progress.verb} Song Processing jobs…` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProgressBar
          animated
          now={progress?.phase === "collecting" ? 100 : Math.max(pct, 3)}
          variant={progress?.phase === "collecting" ? "info" : undefined}
          className="mb-3"
        />
        {progress?.phase === "collecting" ? (
          <p className="mb-2">Preparing durable bulk operation…</p>
        ) : progress ? (
          <>
            <p className="mb-1 fw-semibold" style={{ fontSize: 17 }}>
              {progress.completed.toLocaleString()} /{" "}
              {progress.totalJobs.toLocaleString()} completed
            </p>
            {progress.durable ? (
              <p className="mb-3 text-muted">
                Server-owned BulkOp — progress continues if you refresh or close
                this tab.
              </p>
            ) : progress.totalBatches > 1 ? (
              <p className="mb-3 text-muted">
                Batch {progress.batch.toLocaleString()} of{" "}
                {progress.totalBatches.toLocaleString()}
              </p>
            ) : null}
            <div style={{ lineHeight: 1.7 }}>
              <div>
                Succeeded: <strong>{progress.succeeded.toLocaleString()}</strong>
              </div>
              <div>
                Skipped: <strong>{progress.skipped.toLocaleString()}</strong>
              </div>
              <div>
                Failed: <strong>{progress.failed.toLocaleString()}</strong>
              </div>
            </div>
          </>
        ) : null}
        {progress?.durable ? (
          <p className="text-muted small mb-0 mt-3">
            Discovery, batching, and retry run on the server. Use{" "}
            <strong>Hide and browse other pages</strong> to dismiss this modal —
            a progress chip stays bottom-right, and the delete keeps running.
            Leave Song Processing and come back (or click the chip) to reopen.
          </p>
        ) : (
          <p className="text-muted small mb-0 mt-3">
            Requests are processed in bounded batches of 25 for safety. Please
            keep this page open until the run finishes.
          </p>
        )}
      </Modal.Body>
      {canBackground ? (
        <Modal.Footer>
          <Button
            variant="outline-dark"
            onClick={onContinueInBackground}
            style={{ color: "#111", borderColor: "#333", background: "#fff" }}
          >
            Hide and browse other pages
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
}

// ---- PJL-3.2 completion toast ----------------------------------------------------
// Owner 2026-07-18: after a bulk run completes, no popup that forces another
// click — a toast summarizes the outcome ("✓ 437 Processing Jobs deleted.
// 1 skipped because it is linked to an Approved Track."). Skip reasons are
// grouped and phrased in plain language; raw enums never surface.

/** Plain-language skip phrases, keyed by API reason code. Each entry gives
 * the singular / plural clause following "N skipped because …". */
const SKIP_PHRASES: Record<string, { one: string; many: string }> = {
  "belongs-to-active-song": {
    one: "it belongs to an active Song (manage from Song Library)",
    many: "they belong to active Songs (manage from Song Library)",
  },
  "belongs-to-archived-song": {
    one: "it belongs to an archived Song — restore it from Archived Song Library",
    many: "they belong to archived Songs — restore them from Archived Song Library",
  },
  // Legacy reason (pre-MDL) — keep phrasing so older staging responses still read cleanly.
  "has-approved-artifact-or-track-lineage": {
    one: "it is linked to a living song in Song Library",
    many: "they are linked to living songs in Song Library",
  },
  "job-still-processing": {
    one: "it is still processing",
    many: "they are still processing",
  },
  "job-not-archived": {
    one: "it is not archived yet",
    many: "they are not archived yet",
  },
  "concurrent-modification": {
    one: "it changed while the action ran — retry",
    many: "they changed while the action ran — retry",
  },
};

export interface LifecycleCompletionMessage {
  node: React.ReactElement;
  tone: "success" | "error";
}

/**
 * Builds the completion-toast content for a bulk archive/restore/delete run.
 * tone "error" only when whole batch requests failed (network/5xx) — per-row
 * skips are expected outcomes and stay on the success toast. When
 * `onViewSkipped` is provided (any skips/failures), the toast carries a
 * "View skipped" link that opens the per-row results modal — the operator
 * never has to hunt for what was skipped (owner 2026-07-18).
 */
export function buildLifecycleCompletionMessage(
  verbPast: "Archived" | "Restored" | "Deleted",
  results: LifecycleActionResult[],
  failedCount: number,
  onViewSkipped?: () => void,
): LifecycleCompletionMessage {
  const succeeded = results.filter((row) => row.ok).length;
  const skippedRows = results.filter((row) => !row.ok);

  // Group skips by reason and phrase each group.
  const byReason = new Map<string, number>();
  for (const row of skippedRows) {
    const key = row.reasonCode || "other";
    byReason.set(key, (byReason.get(key) || 0) + 1);
  }
  const skipLines = Array.from(byReason.entries()).map(([reason, n]) => {
    const phrase = SKIP_PHRASES[reason];
    if (phrase) {
      return `${n.toLocaleString()} skipped because ${n === 1 ? phrase.one : phrase.many}.`;
    }
    return `${n.toLocaleString()} skipped (${n === 1 ? "it was" : "they were"} rejected by the server).`;
  });

  const node = (
    <div>
      <div>
        {succeeded.toLocaleString()} Song Processing job{succeeded === 1 ? "" : "s"}{" "}
        {verbPast.toLowerCase()}.
      </div>
      {skipLines.map((line) => (
        <div key={line} className="small">
          {line}
        </div>
      ))}
      {failedCount > 0 ? (
        <div className="small">
          {failedCount.toLocaleString()} failed — those jobs are unchanged;
          retry after checking your connection.
        </div>
      ) : null}
      {onViewSkipped ? (
        <Button
          variant="link"
          size="sm"
          className="p-0 mt-1"
          onClick={onViewSkipped}
        >
          View skipped
        </Button>
      ) : null}
    </div>
  );
  return { node, tone: failedCount > 0 ? "error" : "success" };
}

/** Plain-language outcome for one skipped row (raw reason codes never
 * surface in the UI). */
const SKIP_ROW_COPY: Record<string, string> = {
  "job-still-processing": "Skipped — still processing; wait for it to finish",
  "job-not-archived": "Skipped — not archived; archive it first",
  "belongs-to-active-song":
    "Skipped — belongs to an active Song. Archive or manage the Song from Song Library (Processing mirrors Song lifecycle).",
  "belongs-to-archived-song":
    "Skipped — belongs to an archived Song. Restore the Song from Archived Song Library (or permanently delete it there).",
  "has-approved-artifact-or-track-lineage":
    "Skipped — protected; linked to a living song in Song Library",
  "concurrent-modification": "Skipped — changed concurrently; refresh and retry",
};

/**
 * Per-row results for a bulk lifecycle run. NOT shown automatically — only
 * opened from the completion toast's "View skipped" link when something was
 * skipped or failed.
 */
export function BulkLifecycleResultsModal({
  show,
  title,
  results,
  failedCount,
  onClose,
  onOpenJob,
}: {
  show: boolean;
  title: string;
  results: LifecycleActionResult[] | null;
  /** Jobs whose batch request errored outright (network/5xx), as opposed to
   * per-row skips the server reported. */
  failedCount: number;
  onClose: () => void;
  onOpenJob?: (jobId: string) => void;
}): React.ReactElement {
  const succeeded = results?.filter((r) => r.ok).length ?? 0;
  const rejected = (results?.length ?? 0) - succeeded;
  return (
    <Modal show={show} onHide={onClose} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {results && (
          <>
            <p className="mb-3">
              Succeeded: <strong>{succeeded.toLocaleString()}</strong>
              {" • "}Skipped: <strong>{rejected.toLocaleString()}</strong>
              {" • "}Failed: <strong>{failedCount.toLocaleString()}</strong>
            </p>
            {failedCount > 0 ? (
              <Alert variant="danger" className="mb-3">
                {failedCount.toLocaleString()} job{failedCount === 1 ? "" : "s"}{" "}
                could not be processed because a batch request failed. Those
                jobs are unchanged — retry them after checking your connection.
              </Alert>
            ) : null}
            {/* Skipped rows are what the operator needs to act on — list only
                those; successful jobs are summarized above. */}
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {results
                    .filter((row) => !row.ok)
                    .map((row) => (
                      <tr key={row.jobId}>
                        <td>
                          <JobRef
                            jobId={row.jobId}
                            onOpen={onOpenJob ? () => onOpenJob(row.jobId) : undefined}
                          />
                        </td>
                        <td>
                          <div className="text-danger">
                            {(row.reasonCode && SKIP_ROW_COPY[row.reasonCode]) ||
                              row.message ||
                              "Skipped — rejected by the server"}
                          </div>
                          {row.linkedSong?.songLibraryPath ? (
                            <div className="mt-1">
                              <Link to={row.linkedSong.songLibraryPath}>
                                Open {row.linkedSong.songLibraryLabel}
                                {row.linkedSong.title
                                  ? ` — ${row.linkedSong.title}`
                                  : ""}
                              </Link>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ---- Bulk reprocess results ----------------------------------------------------

export function BulkReprocessResultsModal({
  show,
  response,
  onClose,
  onOpenJob,
}: {
  show: boolean;
  response: BulkReprocessResponse | null;
  onClose: () => void;
  onOpenJob: (jobId: string) => void;
}): React.ReactElement {
  return (
    <Modal show={show} onHide={onClose} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton>
        <Modal.Title>Bulk reprocess results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {response && (
          <>
            <p className="mb-3">
              <strong>{response.requested}</strong> reprocess job
              {response.requested === 1 ? "" : "s"} started
              {response.rejected > 0 ? (
                <>
                  , <strong>{response.rejected}</strong> rejected
                </>
              ) : null}
              .
            </p>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Source Job</th>
                    <th>Outcome</th>
                    <th>New Job</th>
                  </tr>
                </thead>
                <tbody>
                  {response.results.map((row) => (
                    <tr key={row.sourceJobId}>
                      <td>
                        <JobRef jobId={row.sourceJobId} />
                      </td>
                      <td>
                        {row.ok ? (
                          <span className="text-success fw-semibold">
                            Started (v{row.attempt})
                          </span>
                        ) : row.reasonCode === "source-intake-missing" ? (
                          <span className="text-danger">
                            <code>source-intake-missing</code> — original deleted
                            after finalize (production policy); re-upload required
                          </span>
                        ) : (
                          <span className="text-danger">
                            {row.reasonCode ? <code>{row.reasonCode}</code> : "rejected"}
                            {row.message ? ` — ${row.message}` : ""}
                          </span>
                        )}
                      </td>
                      <td>
                        {row.ok && row.jobId ? (
                          <JobRef
                            jobId={row.jobId}
                            onOpen={() => onOpenJob(row.jobId as string)}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
